import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import CategoryFilter from '../components/CategoryFilter.jsx';
import ProductCard from '../components/ProductCard.jsx';
import Pagination from '../components/Pagination.jsx';

export default function RebuildMarketplacePage({ onViewProduct, user, basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket, onShowLogin, onLogout, savedItems, onToggleSave }) {
    const navigate = useNavigate();
    const [products, setProducts] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    // Initialize selection from URL
    const [selectedCategory, setSelectedCategory] = React.useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('category') || 'all';
    });

    // Sync state with URL changes
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const catFromUrl = params.get('category') || 'all';
        if (catFromUrl !== selectedCategory) {
            setSelectedCategory(catFromUrl);
            setCurrentPage(1);
        }
    }, [location.search]);

    // --- NEW: Facet State ---
    const [facets, setFacets] = React.useState({
        brand: '',
        minPrice: '',
        maxPrice: ''
    });

    const [availableFacets, setAvailableFacets] = React.useState({
        brands: {},
        locations: {},
        categories: {}
    });

    const [sortOption, setSortOption] = React.useState('date_desc');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(0);
    const [stickyHeight, setStickyHeight] = React.useState(0); // NEW: State for dynamic sticky height

    // Rebuilds focus on these main categories
    const allowedCategories = ['surface-equipment', 'underground-equipment', 'plant'];

    const fetchProducts = React.useCallback(() => {
        setLoading(true);
        // Use the dedicated rebuild listings endpoint
        const url = new URL("/api/rebuild_listings.php", window.location.origin);
        url.searchParams.append('category', selectedCategory);
        url.searchParams.append('sort', sortOption);
        url.searchParams.append('search', searchTerm);
        // Append Facets
        if (facets.brand) url.searchParams.append('brand', facets.brand);
        if (facets.minPrice) url.searchParams.append('min_price', facets.minPrice);
        if (facets.maxPrice) url.searchParams.append('max_price', facets.maxPrice);

        url.searchParams.append('page', currentPage);
        url.searchParams.append('limit', 50);

        fetch(url, { credentials: 'include' })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                return res.json();
            })
            .then(data => {
                setProducts(data.products);
                setTotalPages(data.pages || 1);
                setTotalItems(data.total || 0);
                if (data.facets) setAvailableFacets(data.facets);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [selectedCategory, sortOption, searchTerm, currentPage, facets]);

    const fetchCategories = React.useCallback(() => {
        fetch(`/api/categories.php?t=${new Date().getTime()}`, { credentials: 'include' })
            .then(res => res.json())
            .then(data => setCategories(data))
            .catch(err => setError(err.message));
    }, []);

    React.useEffect(() => {
        fetchCategories();
        fetchProducts();
    }, [fetchCategories, fetchProducts]);

    const handleCategoryChange = (categorySlug) => {
        setSelectedCategory(categorySlug);
        setCurrentPage(1);
    };

    const handleFacetChange = (key, value) => {
        setFacets(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleClearAll = () => {
        setFacets({brand:'', minPrice:'', maxPrice:''});
        setSearchTerm('');
        setSelectedCategory('all');
        setCurrentPage(1);
    };

    return (
        <div className="min-h-screen">
            <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                setSelectedCategory={handleCategoryChange}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                pageTitle="Equipment Rebuilds"
                pageSubtitle="Source base machines and configure your custom rebuild scope"
                allowedCategories={allowedCategories}
                availableFacets={availableFacets}
                facets={facets}
                onFacetChange={handleFacetChange}
                onClearAll={handleClearAll}
                user={user}
                onShowLogin={onShowLogin}
                onLogout={onLogout}
                basketCount={basket ? basket.reduce((acc, item) => acc + item.quantity, 0) : 0}
                savedItems={savedItems}
                onStickyHeightChange={setStickyHeight} // NEW: Pass callback to update sticky height
            />

            <div className="container mx-auto px-4 pb-8" style={{ paddingTop: `${stickyHeight}px` }}> {/* NEW: Dynamic padding-top */}
                <div className="product-grid-container-mobile">
                    {loading && <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-3xl text-blue-400"></i></div>}
                    {error && <div className="text-center py-10 text-red-500">Error: {error}</div>}
                    
                    {!loading && !error && products.length === 0 && (
                        <div className="text-center py-10 text-gray-500">
                            <p className="text-lg">No rebuild candidates found.</p>
                            <p>Try adjusting your search or category filters.</p>
                        </div>
                    )}

                    {!loading && products.length > 0 && (
                        <div className="product-grid-mobile grid grid-cols-2 md:border-0 md:grid-cols-3 xl:grid-cols-4 md:divide-x-0 md:divide-y-0 md:gap-6">
                            {products.map(product => (
                                <ProductCard 
                                    key={`${product.listingType}-${product.id}`} 
                                    product={product} 
                                    badgeType="standard" // Or a specific 'rebuild' badge if available
                                    onViewDetails={() => onViewProduct({ ...product, badgeType: 'standard' })} 
                                    user={user}
                                    basket={basket}
                                    onAddToBasket={onAddToBasket}
                                    onUpdateBasketQuantity={onUpdateBasketQuantity}
                                    onRemoveFromBasket={onRemoveFromBasket}
                                    onShowLogin={onShowLogin}
                                    savedItems={savedItems}
                                    onToggleSave={onToggleSave}
                                />
                            ))}
                        </div>
                    )}
                    {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
                </div>
            </div>
        </div>
    );
}
